import { useEffect, useState } from 'react';
import { StyleSheet, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { BottomTabBarProps } from '@react-navigation/bottom-tabs';

import { CalloutTooltip } from '@/components/CalloutTooltip';
import { BOTTOM_TABS_HEIGHT } from '@/components/TabBar';
import { useShowOneTime } from '@/hooks/use-show-one-time';

const TAB_BAR_PADDING_H = 8;
type SearchTabTooltipProps = Pick<BottomTabBarProps, 'state' | 'navigation'>;

export const SearchTabTooltip = ({ state }: SearchTabTooltipProps) => {
    const insets = useSafeAreaInsets();

    const { show: showSearchTooltip, handleInteraction: dismissSearchTooltip } = useShowOneTime(
        'Pickem_SearchTab_FeatureTooltip'
    );
    const { width: screenWidth } = useWindowDimensions();

    const [tooltipWidth, setTooltipWidth] = useState(-1);
    //Keep tooltip invisible while layout computed
    const opacity = tooltipWidth > 0 ? 1 : 0;

    const searchTabIndex = state.routes.findIndex(route => route.name === 'FantasySearch');
    const lobbyTabIndex = state.routes.findIndex(route => route.name === 'Lobby');
    const isSearchTabActive = state.index === searchTabIndex;
    const isLobbyActive = state.index === lobbyTabIndex;

    // Check if user is on the root LobbyScreen (not on Promos/FeaturedLineups)
    const lobbyRoute = state.routes[lobbyTabIndex];
    const lobbyState = lobbyRoute?.state;
    const isOnLobbyScreen = !lobbyState || lobbyState.routes[lobbyState.index ?? 0]?.name === 'LobbyScreen';

    const isOnLobbyRoot = isLobbyActive && isOnLobbyScreen;
    const showTooltip = showSearchTooltip && isOnLobbyRoot;

    // Permanently dismiss when navigating to the Feed tab
    useEffect(() => {
        if (isSearchTabActive && showSearchTooltip) {
            dismissSearchTooltip();
        }
    }, [isSearchTabActive, showSearchTooltip, dismissSearchTooltip]);

    if (!showTooltip) {
        return null;
    }

    const totalTabs = state.routes.length;
    const tabWidth = (screenWidth - TAB_BAR_PADDING_H * 2) / totalTabs;
    const searchTabCenterX = TAB_BAR_PADDING_H + searchTabIndex * tabWidth + tabWidth / 2;
    const tooltipLeft = searchTabCenterX - tooltipWidth / 2;

    const tooltipBottom = insets.bottom + BOTTOM_TABS_HEIGHT - 4;

    return (
        <View
            pointerEvents="box-none"
            style={[feedTabTooltipStyles.container, { left: tooltipLeft, bottom: tooltipBottom, opacity }]}
        >
            <CalloutTooltip
                text="Find Search in its new spot in the app bar"
                onClose={dismissSearchTooltip}
                onLayout={ev => {
                    setTooltipWidth(ev.nativeEvent.layout.width);
                }}
                closeTooltipTestId="search-tab-tooltip-close-button"
            />
        </View>
    );
};

const feedTabTooltipStyles = StyleSheet.create({
    container: {
        position: 'absolute',
        zIndex: 10,
    },
});
